import React from "react";
import {
  QuickBooksOperation,
  QuickBooksOperationApiError,
} from "../../types/quickBooksOperations";

interface Props {
  operations: QuickBooksOperation[];
  loading?: boolean;
  error?: QuickBooksOperationApiError | null;
  mutatingId?: string | null;
  onRefresh?: () => void;
  onRetry?: (operation: QuickBooksOperation) => void | Promise<unknown>;
  onReplace?: (operation: QuickBooksOperation) => void | Promise<unknown>;
}

const copy: Record<string, string> = {
  queued: "Queued",
  waiting_dependency: "Waiting for the purchase order",
  preparing: "Preparing",
  submitting: "Sending to QuickBooks",
  retry_wait: "QuickBooks is temporarily unavailable; retry scheduled",
  reconciling: "Confirming the QuickBooks result",
  succeeded: "Created in QuickBooks",
  failed: "Creation failed",
  needs_review: "Confirmation needs administrator review",
  blocked: "Blocked because a required QuickBooks operation failed",
};

const label = (operation: QuickBooksOperation) =>
  operation.document_type === "purchase_order" ? "Purchase order" : "Invoice";

export default function QuickBooksOperationsStatus({
  operations,
  loading,
  error,
  mutatingId,
  onRefresh,
  onRetry,
  onReplace,
}: Props) {
  if (!loading && operations.length === 0 && !error) return null;
  const unauthorized = error?.status === 401 || error?.status === 403;

  return (
    <section
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="mt-3 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm"
    >
      {loading && operations.length === 0 ? <p>Loading QuickBooks status…</p> : null}
      {operations.map((operation) => {
        const mutating = mutatingId === operation.operation_id;
        return (
          <div key={operation.operation_id} className="rounded border border-gray-200 bg-white p-2">
            <p className="font-semibold">{label(operation)}</p>
            <p>{operation.message || copy[operation.status]}</p>
            {operation.document_number ? <p>Number: {operation.document_number}</p> : null}
            {operation.status === "needs_review" ? (
              <p className="mt-1 text-amber-700">Do not submit another document. An administrator must verify QuickBooks first.</p>
            ) : null}
            <div className="mt-2 flex gap-2">
              {operation.can_retry && onRetry ? (
                <button
                  type="button"
                  className="rounded bg-gray-800 px-3 py-1 text-white disabled:opacity-60"
                  disabled={Boolean(mutatingId)}
                  onClick={() => void onRetry(operation)}
                >
                  {mutating ? "Retrying…" : `Retry ${label(operation).toLowerCase()}`}
                </button>
              ) : null}
              {operation.can_replace && onReplace ? (
                <button
                  type="button"
                  className="rounded bg-amber-700 px-3 py-1 text-white disabled:opacity-60"
                  disabled={Boolean(mutatingId)}
                  onClick={() => void onReplace(operation)}
                >
                  {mutating ? "Replacing…" : `Replace ${label(operation).toLowerCase()}`}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
      {error ? (
        <div>
          <p>{unauthorized ? "You are not authorized to view this QuickBooks operation." : error.message}</p>
          {!unauthorized && onRefresh ? (
            <button
              type="button"
              className="mt-2 rounded bg-gray-800 px-3 py-1 text-white"
              disabled={loading || Boolean(mutatingId)}
              onClick={onRefresh}
            >
              Refresh status
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
